import { storageCoordinator } from "./system/coordinator";
import {
  ensureLegacyDocumentsMigrated,
  type LegacyMigrationResult,
} from "./projectStorageMigration";
import { bootOrphanSweep, type OrphanSweepReport } from "./projectStorageSweep";
import { runBootAssetGc, type BootAssetGcResult } from "./assets/assetGarbageCollect";
import { indexedDBManager } from "./system/indexed-db";
import type { RecoverySeedReport } from "./system/types";
import { logger } from "../utils/logger";

/**
 * Boot-time storage-foundation bootstrap — Bombproof storage foundation,
 * step 6 (board 12-imgstore; approval-gate §7).
 *
 * Runs the boot-time reconcile passes in the ONLY safe order (NA-B2″): the
 * one-time legacy localStorage→IndexedDB document migration FIRST, the orphan
 * sweep AFTER. Since hotfix-sweep-s1 the orphan sweep is REPORT-ONLY — it logs
 * the records of projects missing from the list and deletes nothing (the list can
 * lose a live project under a two-tab race; see `bootOrphanSweep`). The sweep must never run before an
 * un-migrated LS-only document has been copied to IndexedDB — reordering the
 * two is the exact adversary path that deletes the only copy of a legacy
 * document.
 *
 * SWEEP REFUSAL ON AMBIGUITY (D1 guard): the orphan sweep's live-project set
 * comes from the UNFILTERED project registry (`stagecraft_projects`). A
 * user-filtered list would classify other users' projects as orphans; an
 * unreadable registry would classify EVERY project as an orphan. So the sweep
 * runs ONLY when the registry loads successfully as an array — on any failure
 * or non-array shape it REFUSES loudly and leaves all records in place (never
 * delete on ambiguity; the per-delete sweep in projectManager remains the
 * primary reclaim path). An affirmatively-empty registry (successful load,
 * zero entries) DOES run the sweep, which reports every `project_*` record.
 * (Before hotfix-sweep-s1 the sweep deleted what it found; the registry proved
 * not to be a safe liveness authority, so it now deletes nothing.)
 *
 * Invoked at boot from `main.tsx` (imperative chokepoint — never
 * React-effect-order-dependent). Document entry points additionally await the
 * migration singleton themselves (`projectManager.loadProjectData` /
 * `getProjectList`), so no document read can beat the migration even if this
 * bootstrap is still in flight.
 */

const PROJECT_REGISTRY_KEY = "stagecraft_projects";

export interface StorageFoundationBootResult {
  migration: LegacyMigrationResult;
  /**
   * The eager recovery-ring seed's report (snapshots-p2a-s10 D7), or the error it threw.
   * A seed failure never blocks boot; the lazy seed inside an armed write is the fallback.
   */
  recoverySeed: RecoverySeedReport | { error: string };
  /** The report-only sweep's findings, or null when the sweep was refused. */
  sweep: OrphanSweepReport | null;
  sweepRefusedReason: "registry-unreadable" | null;
  /**
   * Per-asset GC result (imgstore-gc-s1). Null when the doc sweep was refused
   * (registry unreadable ⇒ we never mark against an authoritative corpus) — GC
   * inherits that refusal.
   */
  assetGc: BootAssetGcResult | null;
}

export async function bootstrapStorageFoundation(): Promise<StorageFoundationBootResult> {
  // 1. Migrate FIRST — NA-B2″ ordering (see module doc).
  const migration = await ensureLegacyDocumentsMigrated();

  // 1b. Eager recovery-ring seed AFTER the migration, BEFORE the sweep and GC
  //     (snapshots-p2a-s10 D7; CTO 65-81 (a)). Caught and recorded — never thrown.
  let recoverySeed: StorageFoundationBootResult["recoverySeed"];
  try {
    recoverySeed = await indexedDBManager.seedRecoveryRings();
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    logger.error("storageFoundationBoot: recovery-ring seed FAILED (non-fatal; boot continues)", {
      error: err,
    });
    recoverySeed = { error: err.message };
  }

  // 2. Live-project ids from the UNFILTERED registry; refuse on ambiguity.
  let liveProjectIds: string[] | null = null;
  try {
    const registry =
      await storageCoordinator.load<Array<{ id?: unknown }>>(
        PROJECT_REGISTRY_KEY,
      );
    if (registry.success && Array.isArray(registry.data)) {
      liveProjectIds = registry.data
        .map((entry) => (typeof entry?.id === "string" ? entry.id : null))
        .filter((id): id is string => id !== null);
    }
  } catch {
    // Unreadable registry — refusal below.
  }

  if (liveProjectIds === null) {
    logger.warn(
      "storageFoundationBoot: project registry unreadable — orphan sweep REFUSED (never delete on ambiguity)",
    );
    return {
      migration,
      recoverySeed,
      sweep: null,
      sweepRefusedReason: "registry-unreadable",
      assetGc: null,
    };
  }

  // 3. Orphan sweep AFTER migration (NA-B2″) — REPORT-ONLY, deletes nothing.
  const sweep = await bootOrphanSweep(liveProjectIds);

  // 4. Per-asset GC AFTER the document sweep (imgstore-gc-s1): the referenced set
  //    is marked against EVERY surviving `project_*` document — since the sweep is
  //    report-only, that includes projects missing from the list, so their blobs
  //    are kept (hotfix-sweep-s1). GC assembles a COMPLETE referenced set or refuses (never
  //    deletes on ambiguity); the `liveProjectIds` here is the authoritative,
  //    successfully-loaded registry the same D1 guard above gates on. Since
  //    hotfix-sweep-s2 the GC is REPORT-ONLY too: it logs the blobs it would
  //    delete and deletes none (another tab's unsaved blob is invisible to it).
  const assetGc = await runBootAssetGc(liveProjectIds);

  return { migration, recoverySeed, sweep, sweepRefusedReason: null, assetGc };
}
